import { DataTable } from '@/components/DataTable';
import { OneLineText } from '@/components/OneLineText';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { capitalizeFirstLetter, downloadFilefromBuffer, formatPhoneNumber } from '@/lib/utils';
import { getContactsCsv } from '@/services/clients';

import type { ClientResponse, ContactResponse } from '@/types/client.types';
import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { z } from 'zod';
export const ContactGetAllCSVResponseSchema = z.string();
export const ExportClientContactsDialog = ({
  onClose,
  clientData,
}: {
  onClose: () => void;
  clientData?: ClientResponse;
}) => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const handleDownloadCSV = async () => {
    setIsDownloading(true);
    const today = new Date();
    try {
      const { data, error } = await getContactsCsv(clientData?.clientId ?? '');

      if (data) {
        const fileName = `${clientData?.clientName}_Contacts_Master_List_${today.getFullYear()}${(today.getMonth() + 1)
          .toString()
          .padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}`;
        downloadFilefromBuffer(data, `${fileName}.csv`, 'CSV');
      } else if (error) {
        displayError(error, 5000);
      }
    } catch (err) {
      toast({
        description: 'Download failed. Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };
  const columns: ColumnDef<ContactResponse>[] = [
    {
      accessorKey: 'name',
      header: 'Contact Name',

      size: 180,
      cell: ({ row }) => {
        return (
          <OneLineText className="mr-5 min-w-[150px]" text={row.original.firstName + ' ' + row.original.lastName} />
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Employee Type',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={capitalizeFirstLetter(row.original.type) ?? '-'} />;
      },
    },
    {
      accessorKey: 'email',
      header: 'Email Address',
      size: 120,
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={row.original.email ?? '-'} />;
      },
    },
    {
      accessorKey: 'phone',
      header: 'Phone Number',
      cell: ({ row }) => {
        return (
          <OneLineText
            className="mr-5 min-w-[100px]"
            text={row.original.phone ? formatPhoneNumber(row.original.phone) : '-'}
          />
        );
      },
    },
    {
      accessorKey: 'extension',
      header: 'Extension',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px]" text={row.original.extension ?? '-'} />;
      },
    },
    {
      accessorKey: 'fax',
      header: 'Fax Number',
      cell: ({ row }) => {
        return <div className="min-w-[200px] text-sm">{row.original.fax ?? '-'}</div>;
      },
    },
    {
      accessorKey: 'cell',
      header: 'Cell Number',
      cell: ({ row }) => {
        return (
          <OneLineText
            className="mr-5 min-w-[200px]"
            text={row.original.cell ? formatPhoneNumber(row.original.cell) : '-'}
          />
        );
      },
    },
    {
      accessorKey: 'lawyerBarId',
      header: 'Lawyer Bar ID',
      cell: ({ row }) => {
        return <OneLineText className="mr-5 min-w-[100px] text-sm" text={row.original.lawyerBarId ?? '-'} />;
      },
    },
    {
      accessorKey: 'sendAffidavit',
      header: 'Send Affidavit',
      cell: ({ row }) => {
        const affidavitMails = (clientData?.affidavit ?? []).concat(clientData?.affidavitCc ?? []);
        return (
          <OneLineText
            className="mr-5 min-w-[150px] text-sm"
            text={affidavitMails?.includes(row.original.email) ? 'Yes' : 'No'}
          />
        );
      },
    },
    {
      accessorKey: 'emailStatus',
      header: 'Email Status',
      cell: ({ row }) => {
        return (
          <OneLineText
            className="mr-5 min-w-[100px] text-sm"
            text={capitalizeFirstLetter(row.original.emailStatus) ?? '-'}
          />
        );
      },
    },
  ];

  return (
    <Sheet modal={true} open={true}>
      <SheetContent
        hideCloseButton={true}
        className="md:w-[80vw] w-[90vw] md:gap-12 md:max-h-[80vh] max-h-[100vh] h-fit flex flex-col scrollbar-hide focus:outline-none rounded-lg gap-4 p-8"
        tabIndex={-1}
        side={'center'}
      >
        <div className="flex-grow min-h-0 gap-4 md:gap-8 flex flex-col">
          <SheetTitle>Preview - {clientData?.clientName}&#39;s Contact List</SheetTitle>
          <DataTable
            columns={columns}
            rows={clientData?.contacts?.slice(0, 1000) ?? []}
            showPagination={false}
            stickyHeader
            className=" max-h-[44vh] min-h-[10px] h-fit scrollbar-hide overflow-auto custom-scrollbar"
            showNRecords
            customPageSize={1000}
          />
        </div>
        <div className="w-full flex flex-col gap-4 md:flex-row justify-end ">
          <Button
            type="button"
            variant="outline"
            className="text-primary w-[75px] h-[36px] py-2 px-4 rounded-md focus:outline-none border-primary hover:bg-[#EDF5FF]"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            variant="outline"
            className="text-[#fff] h-[36px] w-fit min-w-[135px] py-2 px-4 rounded-md bg-primary border-primary hover:bg-[#12375C] hover:text-white"
            onClick={handleDownloadCSV}
            disabled={clientData?.contacts?.length === 0 || isDownloading}
            isLoading={isDownloading}
          >
            Download CSV
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
